import { BM_STATUS_OPTIONS, DEFAULT_GB_INDEX_FIELDS } from '@/constants/benchmarks/IndexConstants.ts';
import { IGbIndex } from '@/interfaces/benchmarks/index/IIndex.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import {
    IBenchmarkPoolAssetFilter,
    IBenchmarkPoolDefinition,
    IBenchmarkPoolDefinitionInput,
    IBenchmarkPoolRecord,
    ILeaderboardBenchmarkPoolCategory,
    ILeaderboardBenchmarkPoolConfig,
} from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { cloneDeep } from '@/utils/global.ts';

// Wire values from Enact DataDownloader.Common FuelTypeEnum, ZoneEnum and BmuStatus.
// Keep these explicit: fuel values have gaps and must never depend on array position.
const FUEL_NAMES: Record<number, string> = {
    0: 'Other',
    1: 'Biomass',
    2: 'CCGT',
    3: 'CHP',
    4: 'Coal',
    5: 'OCGT',
    6: 'Hydro',
    7: 'Nuclear',
    8: 'Wind',
    9: 'PumpedStorage',
    11: 'Supply',
    12: 'Battery',
    13: 'InterconnectorEastWest',
    14: 'InterconnectorFrance',
    15: 'InterconnectorIreland',
    16: 'InterconnectorNetherlands',
    17: 'InterconnectorNemo',
    18: 'Oil',
    19: 'InterconnectorElecLink',
    20: 'InterconnectorIfa2',
    21: 'BSAD',
    22: 'InterconnectorNorthSeaLink',
    23: 'GasRecip',
    24: 'Solar',
    25: 'InterconnectorVikingLink',
    26: 'InterconnectorGreenlink',
    27: 'SyncCon',
};
// An empty editor fuel selection means the whole pool, not an empty backend fuel list.
export const ALL_BENCHMARK_POOL_FUELS = Object.values(FUEL_NAMES);

const ZONE_NAMES = [
    ...Array.from({ length: 17 }, (_, index) => `Z${index + 1}`),
    ...Array.from({ length: 17 }, (_, index) => `B${index + 1}`),
    'N',
];
const BM_STATUS_NAMES = ['Bm', 'BmNoFpn', 'NonBm'];
const UNKNOWN_SELECTION = 'Unknown';

function enumName (value: string | number, names: Record<number, string> | Array<string>): string {
    if (typeof value === 'string') return value;
    const name = names[value];
    if (name === undefined) throw new Error(`Unsupported benchmark pool filter value: ${value}`);
    return name;
}

function selections (
    values: Array<string | number | null> | undefined,
    names: Record<number, string> | Array<string>,
    allLabel?: string,
): Array<IIdName> {
    if (!values?.length) return allLabel ? [{ id: allLabel, name: allLabel }] : [];
    return values.map(value => {
        const id = value === null ? UNKNOWN_SELECTION : enumName(value, names);
        return { id, name: names === BM_STATUS_NAMES ? BM_STATUS_OPTIONS.find(option => option.id === id)?.name ?? id : id };
    });
}

export function benchmarkPoolAssetFilterToIndex (filter: IBenchmarkPoolAssetFilter): IGbIndex {
    const defaults = cloneDeep(DEFAULT_GB_INDEX_FIELDS);
    return {
        selectedFuel: selections(filter.selectedFuels, FUEL_NAMES),
        selectedZone: selections(filter.selectedZones, ZONE_NAMES, 'All Zones'),
        selectedBmStatus: selections(filter.selectedBmStatuses, BM_STATUS_NAMES, 'All'),
        selectedOwners: selections(filter.selectedOwners, {}, 'All Owners'),
        selectedOptimisers: selections(filter.selectedOptimisers, {}, 'All Optimisers'),
        selectedCapacity: [filter.minimumCapacityMw ?? defaults.selectedCapacity[0], filter.maximumCapacityMw ?? defaults.selectedCapacity[1]],
        selectedDuration: [filter.minimumDurationHours ?? defaults.selectedDuration[0], filter.maximumDurationHours ?? defaults.selectedDuration[1]],
        selectedPlants: [...(filter.selectedPlants ?? [])],
    };
}

function selectionIds (items: Array<IIdName>, allLabel: string): Array<string> {
    return items.some(item => item.id === allLabel) ? [] : items.map(item => item.id);
}

export function indexToBenchmarkPoolAssetFilter (index: IGbIndex): IBenchmarkPoolAssetFilter {
    if (index.selectedPlants.length) {
        return { selectedPlants: [...index.selectedPlants], restrictToSelectedPlants: true, includeColocation: true };
    }
    return {
        selectedPlants: [],
        selectedFuels: index.selectedFuel.map(item => item.id),
        selectedZones: selectionIds(index.selectedZone, 'All Zones').map(id => (id === UNKNOWN_SELECTION ? null : id)),
        selectedBmStatuses: selectionIds(index.selectedBmStatus, 'All').map(id => (id === UNKNOWN_SELECTION ? null : id)),
        selectedOwners: selectionIds(index.selectedOwners, 'All Owners'),
        selectedOptimisers: selectionIds(index.selectedOptimisers, 'All Optimisers'),
        minimumCapacityMw: index.selectedCapacity[0] ?? null,
        maximumCapacityMw: index.selectedCapacity[1] ?? null,
        minimumDurationHours: index.selectedDuration[0] ?? null,
        maximumDurationHours: index.selectedDuration[1] ?? null,
        includeColocation: true,
        restrictToSelectedPlants: false,
    };
}

function categoryAssetFilter (category: ILeaderboardBenchmarkPoolCategory): IBenchmarkPoolAssetFilter {
    if (!category.assetFilter) return indexToBenchmarkPoolAssetFilter(category.indexFilters);
    const filter = cloneDeep(category.assetFilter);
    const originalIndex = benchmarkPoolAssetFilterToIndex(filter);
    const next = indexToBenchmarkPoolAssetFilter({ ...category.indexFilters, selectedPlants: [] });
    const fields: Array<[keyof IGbIndex, Array<keyof IBenchmarkPoolAssetFilter>]> = [
        ['selectedFuel', ['selectedFuels']],
        ['selectedZone', ['selectedZones']],
        ['selectedBmStatus', ['selectedBmStatuses']],
        ['selectedOwners', ['selectedOwners']],
        ['selectedOptimisers', ['selectedOptimisers']],
    ];
    fields.forEach(([indexKey, filterKeys]) => {
        if (JSON.stringify(category.indexFilters[indexKey]) === JSON.stringify(originalIndex[indexKey])) return;
        filterKeys.forEach(key => { Object.assign(filter, { [key]: next[key] }); });
    });
    const ranges: Array<['selectedCapacity' | 'selectedDuration', Array<keyof IBenchmarkPoolAssetFilter>]> = [
        ['selectedCapacity', ['minimumCapacityMw', 'maximumCapacityMw']],
        ['selectedDuration', ['minimumDurationHours', 'maximumDurationHours']],
    ];
    ranges.forEach(([indexKey, filterKeys]) => {
        filterKeys.forEach((key, bound) => {
            if (category.indexFilters[indexKey][bound] === originalIndex[indexKey][bound]) return;
            Object.assign(filter, { [key]: next[key] });
        });
    });
    if (JSON.stringify(category.indexFilters.selectedPlants) !== JSON.stringify(originalIndex.selectedPlants)) {
        filter.selectedPlants = [...category.indexFilters.selectedPlants];
        filter.restrictToSelectedPlants = filter.selectedPlants.length > 0;
    }
    return filter;
}

export function benchmarkPoolDefinitionToRecord (definition: IBenchmarkPoolDefinition): IBenchmarkPoolRecord {
    return {
        id: definition.id,
        name: definition.name,
        config: {
            benchmarkPlantId: definition.benchmarkPlantId,
            requiredPoolSize: definition.requiredPoolSize,
            categories: [...definition.categories]
                .sort((a, b) => a.priority - b.priority || (a.id < b.id ? -1 : Number(a.id > b.id)))
                .map(category => ({
                    id: category.id,
                    name: category.name ?? undefined,
                    priority: category.priority,
                    assetFilter: cloneDeep(category.assetFilter),
                    indexFilters: benchmarkPoolAssetFilterToIndex(category.assetFilter),
                })),
        },
    };
}

export function benchmarkPoolRecordToInput (record: {
    name: string;
    config: ILeaderboardBenchmarkPoolConfig;
}): IBenchmarkPoolDefinitionInput {
    const { config } = record;
    if (!record.name.trim() || !config.benchmarkPlantId.trim() || config.requiredPoolSize < 1 || !config.categories.length) {
        throw new Error('A complete named benchmark pool definition is required before saving.');
    }
    const sorted = [...config.categories].sort((a, b) => (
        (a.priority ?? 0) - (b.priority ?? 0) || (a.id < b.id ? -1 : Number(a.id > b.id))
    ));
    const preservePriorities = config.categories.every((category, index) => (
        category.priority !== undefined && sorted[index].id === category.id
    ));
    return {
        name: record.name.trim(),
        benchmarkPlantId: config.benchmarkPlantId,
        requiredPoolSize: config.requiredPoolSize,
        categories: config.categories.map((category, index) => ({
            id: category.id,
            name: category.name,
            priority: preservePriorities ? category.priority! : index + 1,
            assetFilter: categoryAssetFilter(category),
        })),
    };
}
